'use client';
import { useRouter } from '@bprogress/next';
import FeaturedBlogCard from '@c/cards/FeaturedBlogCard';
import BlogHeader from '@c/headers/blog/BlogHeader';
import Button from '@ui/Button';
import BlogLink from '@ui/Link/BlogLink';
import Select from '@ui/Select';
import { CursorPagination } from '@util/firestore/blog/blog.server';
import { BlogDocument } from '@util/types/firestore/blog';
import { encodeQueryData } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { useEffect, useState } from 'react';
const blogTags = [
  'Community',
  'Videos',
  "How To's",
  'News & Press',
  'Seller Spotlight',
  'Selling',
  'Buying',
] as const;
const categories = ['All Categories', ...blogTags];
interface BlogPageProps {
  posts: BlogDocument[];
  featuredBlog: BlogDocument | null;
  pageInfo: CursorPagination<string>;
  category: string | null;
}
const BlogPage = ({
  posts,
  featuredBlog,
  pageInfo,
  category: initialCategory,
}: BlogPageProps) => {
  const { userDoc } = useAuth();
  const router = useRouter();
  const isFirstPage = pageInfo.prev === null;
  const emptyPosts = posts.length === 0;
  const showPaginator = pageInfo.totalResults > 6;
  const [category, setCategory] = useState(initialCategory ?? 'All Categories');
  useEffect(() => {
    // update the url with the category
    let queryValues = {};
    if (category !== 'All Categories') {
      queryValues = { ...queryValues, category };
    }
    if (pageInfo.currentCursor) {
      if (!isFirstPage)
        queryValues = { ...queryValues, cursor: pageInfo.currentCursor };
    }
    if (pageInfo.direction) {
      // if first page, dont add direction to url
      if (!isFirstPage)
        queryValues = { ...queryValues, direction: pageInfo.direction };
    }
    const query = encodeQueryData(queryValues);
    const path = window.location.pathname;
    let newPath = query ? `${path}?${query}` : path;
    router.replace(newPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, pageInfo.direction, pageInfo.currentCursor]);
  function handleCategoryChange(category: string) {
    setCategory(category);
  }
  return (
    <>
      {userDoc?.roles?.blog && (
        <Button href="/blog/create" text="Create Blog" type="primary" />
      )}
      <BlogHeader />
      <div className="mx-auto mb-[2rem] mt-[6rem] w-full max-w-[120rem]">
        {/* top bar */}
        <div className="mx-4 flex flex-col justify-between gap-[1.6rem] sm:mx-0 sm:flex-row sm:items-center sm:gap-0">
          {/* controls */}
          <div className="flex h-fit w-full max-w-[67.4rem] flex-col gap-[1.4rem] sm:flex-row sm:justify-end">
            {/* TODO: enable search */}
            {/* <Input
          type="search"
          placeholder="Find blog article ..."
          full={true}
        /> */}
            <div>
              <Select
                value={category}
                onChange={(cat) => handleCategoryChange(cat.value)}
                options={categories.map((c) => ({
                  label: c,
                  value: c,
                  id: c,
                }))}
                allowEmpty={false}
              />
            </div>
          </div>
        </div>
        {/* Empty */}
        {emptyPosts && (
          <div className="mt-[4rem] flex flex-col items-center justify-center sm:mt-[6rem]">
            <h1 className="text-[2.4rem] font-semibold sm:text-[3.2rem]">
              No results found
            </h1>
            <p className="mt-[1.6rem] text-[1.6rem] text-brand-gray sm:text-[2rem]">
              Try searching for something else
            </p>
          </div>
        )}
        {/* results */}
        {!emptyPosts && (
          <div className="mx-4 mt-[4rem] sm:mt-[6rem] lg:mx-0">
            {/* featured post */}
            {featuredBlog && (
              <div
                key={featuredBlog.id}
                className="mb-[4rem] w-full grow lg:mb-[6rem]"
              >
                <FeaturedBlogCard blog={featuredBlog} />
              </div>
            )}
            {/* other posts */}
            <div className="flex flex-col flex-wrap gap-[2.4rem] sm:flex-row sm:justify-center">
              {posts.map((post) => {
                return <BlogLink key={post.id} blog={post} />;
              })}
            </div>
          </div>
        )}
        {/* pagination */}
        {showPaginator && (
          // <Paginator currentPage={1} totalPages={1} />
          <div className="mt-[4rem] flex w-full">
            {pageInfo.prev && (
              <Button
                text="Prev"
                type="secondary"
                disabled={!pageInfo.prev}
                href={`/blog?direction=prev&cursor=${pageInfo.prev}`}
              />
            )}
            {pageInfo.next && (
              <div className="ml-auto">
                <Button
                  text="Next"
                  type="secondary"
                  disabled={!pageInfo.next}
                  href={`/blog?direction=next&cursor=${pageInfo.next}`}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default BlogPage;
